import React from "react";
import { Link } from "react-router-dom";
import {
  Route, GitBranch, Flame, Zap, Rocket, Crosshair, Wind, Target, Layers, Anchor, Expand, CornerDownRight, Sprout,
  Swords, Footprints, LifeBuoy, ShieldCheck, Move, Castle, AlertTriangle, Shield, Fingerprint, Clapperboard,
} from "lucide-react";
import { SIDE_COLOR, getArchetype } from "../../lib/archetypes";
import { FitRing, FitBreakdown } from "./ArchetypeFit";
import { ArchetypeFlowLists } from "./ArchetypeFlow";
import { archetypeSlug } from "../../lib/archetypeFlow";

const BY_NAME = {
  "Primary Ball Handler": Route, "Secondary Ball Handler": GitBranch, "Shot Creator": Flame, "Slasher": Zap, "Athletic Finisher": Rocket,
  "Off-Screen Shooter": Crosshair, "Movement Shooter": Wind, "Stationary Shooter": Target, "Versatile Big": Layers, "Post Scorer": Anchor,
  "Stretch Big": Expand, "Roll & Cut Big": CornerDownRight, "Developing / Not Yet Defined": Sprout,
  "Point of Attack": Swords, "Chaser": Footprints, "Helper": LifeBuoy, "Wing Stopper": ShieldCheck, "Mobile Big": Move, "Anchor Big": Castle, "Low Activity": AlertTriangle,
};
export const archetypeIcon = (a, side) => (a && BY_NAME[a.name]) || (side === "defense" ? Shield : Target);

// Identity card: big icon badge, Primary as the headline, Secondary as a companion line. One per side.
export function ArchetypeIdentityCard({ side, label, sel, fits, compact = false }) {
  const p = getArchetype(sel?.primary), s = getArchetype(sel?.secondary);
  const color = SIDE_COLOR[side];
  const PIcon = archetypeIcon(p, side), SIcon = archetypeIcon(s, side);
  const pFit = p && fits?.[p.id], sFit = s && fits?.[s.id];
  return (
    <div className="relative overflow-hidden border border-white/10 bg-[#141414]" data-testid={`archetype-summary-${side}`}
      style={{ backgroundImage: `radial-gradient(60% 80% at 0% 0%, ${color}22 0%, transparent 60%)` }}>
      <div className="absolute inset-y-0 left-0 w-[3px]" style={{ background: color }} />
      <div className={`flex items-center gap-3 ${compact ? "px-4 pt-3" : "px-6 pt-5"}`}>
        <span className="text-[10px] uppercase tracking-[0.45em] font-bold" style={{ color }}>{label}</span>
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">Archetype</span>
      </div>
      {!p ? (
        <div className={`${compact ? "px-4 py-5" : "px-6 py-8"} flex items-center gap-4`} data-testid={`archetype-summary-${side}-empty`}>
          <div className="w-14 h-14 rounded-full border border-dashed border-white/15 flex items-center justify-center shrink-0"><Fingerprint className="w-6 h-6 text-[#575757]" /></div>
          <div><div className="text-sm text-[#a0a0a0] font-semibold">Not defined yet</div><div className="text-xs text-[#575757]">Who they are as a player will show here.</div></div>
        </div>
      ) : (
        <div className={`${compact ? "px-4 py-4" : "px-5 py-5"} flex items-start gap-4`}>
          <div className="relative shrink-0">
            <div className={`${compact ? "w-12 h-12" : "w-14 h-14"} rounded-full flex items-center justify-center border`} style={{ borderColor: `${color}66`, background: `${color}1a`, boxShadow: `0 0 0 6px ${color}10, 0 0 40px ${color}22` }}>
              <PIcon className={compact ? "w-5 h-5" : "w-6 h-6"} style={{ color }} />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-[0.35em] text-[#757575]">Primary</div>
            <div className={`font-heading uppercase tracking-wide text-white leading-none mt-1 ${compact ? "text-lg" : "text-xl lg:text-2xl"}`} data-testid={`archetype-summary-${side}-primary`}>{p.name}</div>
            {p.description && <div className="text-[#a0a0a0] leading-snug mt-1.5 text-xs">{p.description}</div>}
            {!compact && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <ArchetypeFlowLists archetypeId={p.id} side={side} compact color={color} testid={`archetype-flow-${side}`} />
                <Link to={`/player-archetypes/${archetypeSlug(p.name)}`} className="inline-flex items-center gap-1.5 mt-3 text-[9px] uppercase tracking-[0.25em] font-bold hover:underline" style={{ color }} data-testid={`archetype-film-link-${side}`}><Clapperboard className="w-3 h-3" /> Film study: {p.name}</Link>
              </div>
            )}
            {!compact && <FitBreakdown fit={pFit} testid={`archetype-fit-breakdown-${side}`} />}
            {s && (
              <div className="mt-3 pt-3 border-t border-white/10 flex items-start gap-3" data-testid={`archetype-summary-${side}-secondary`}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center border border-white/15 bg-white/[0.03] shrink-0"><SIcon className="w-3.5 h-3.5 text-[#e0e0e0]" /></div>
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.35em] text-[#757575]">Secondary</div>
                  <div className="text-sm text-white font-semibold leading-tight">{s.name}</div>
                  {s.description && <div className="text-[11px] text-[#a0a0a0] leading-snug mt-0.5">{s.description}</div>}
                </div>
                {sFit && <div className="ml-auto flex items-center gap-1.5 shrink-0" title={sFit.reason} data-testid={`archetype-fit-secondary-${side}`}><span className="text-xs font-bold tabular-nums text-white">{sFit.fit}%</span><span className="text-[8px] uppercase tracking-[0.25em] font-bold" style={{ color: { Strong: "#4ade80", Moderate: "#facc15", Weak: "#f87171" }[sFit.label] }}>{sFit.label}</span></div>}
              </div>
            )}
          </div>
          {pFit && <FitRing fit={pFit} size={compact ? 48 : 60} testid={`archetype-fit-${side}`} />}
        </div>
      )}
    </div>
  );
}
